import type { MiSolicitud } from '@/lib/supabase/tipos-bd'
import { fechaHora, horas as enHoras } from '@/shared/utilidades/formato'

import estilos from './ListaDeSolicitudes.module.css'

/**
 * Lo que el operario pidió y en qué va.
 *
 * Vive dentro de su tablero, no en otra pantalla: él tiene UNA pantalla, y
 * esto es lo segundo que mira después de su trabajo. Hasta ahora pedía y
 * quedaba esperando sin saber si le habían respondido — la decisión se
 * guardaba con nombre y fecha, pero nunca se le devolvía a quien preguntó.
 *
 * LA RESPUESTA VIENE CON NOMBRE. Un «negado» sin quién ni por qué no se puede
 * discutir con nadie, y lo que consigue es que la próxima vez no se pida.
 */

const NOMBRE: Record<MiSolicitud['tipo'], string> = {
  mas_tiempo: 'Más tiempo',
  inicio_sin_ubicacion: 'Empezar sin ubicación',
}

const SITUACION: Record<MiSolicitud['situacion'], string> = {
  esperando: 'Esperando respuesta',
  aprobada: 'Aprobada',
  negada: 'Negada',
  // Hay decisión registrada pero sin la huella que distingue aprobar de
  // negar. Se dice lo que se sabe en vez de inventar un rechazo.
  respondida: 'Ya respondida',
}

export function ListaDeSolicitudes({ solicitudes }: { solicitudes: MiSolicitud[] }) {
  if (solicitudes.length === 0) return null

  const esperando = solicitudes.filter((s) => s.situacion === 'esperando')
  const resueltas = solicitudes.filter((s) => s.situacion !== 'esperando')

  return (
    <section className={estilos.zona}>
      <p className={estilos.titulo}>
        {esperando.length > 0
          ? `Sus solicitudes · ${esperando.length} sin responder`
          : 'Sus solicitudes'}
      </p>

      {esperando.map((solicitud) => (
        <Ficha key={`${solicitud.tipo}-${solicitud.orden_trabajo_id}`} solicitud={solicitud} />
      ))}

      {resueltas.length > 0 && (
        <>
          {esperando.length > 0 && <p className={estilos.separador}>Ya respondidas</p>}
          {resueltas.map((solicitud) => (
            <Ficha key={`${solicitud.tipo}-${solicitud.orden_trabajo_id}`} solicitud={solicitud} />
          ))}
        </>
      )}
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

function Ficha({ solicitud }: { solicitud: MiSolicitud }) {
  return (
    <article className={`${estilos.ficha} ${estilos[solicitud.situacion]}`}>
      <header className={estilos.encabezado}>
        <span className={estilos.tipo}>{NOMBRE[solicitud.tipo]}</span>
        <span className={estilos.situacion}>{SITUACION[solicitud.situacion]}</span>
      </header>

      <p className={estilos.orden}>
        <span className={estilos.numero}>{solicitud.numero}</span>
        {solicitud.cliente && <span className={estilos.cliente}>{solicitud.cliente}</span>}
      </p>

      {solicitud.horas_pedidas !== null && (
        <p className={estilos.pedido}>
          Pidió <b>{enHoras(solicitud.horas_pedidas)}</b> más
        </p>
      )}

      {solicitud.justificacion && (
        <p className={estilos.justificacion}>«{solicitud.justificacion}»</p>
      )}

      {/* La respuesta, con nombre y fecha. Sin eso no se puede reclamar. */}
      {solicitud.situacion !== 'esperando' && (
        <div className={estilos.respuesta}>
          {solicitud.respuesta && <p className={estilos.respuestaTexto}>{solicitud.respuesta}</p>}

          <p className={estilos.firma}>
            {solicitud.decidida_por ?? 'El Gerente operativo'}
            {solicitud.decidida_en && <> · {fechaHora(solicitud.decidida_en)}</>}
          </p>
        </div>
      )}

      {solicitud.situacion === 'esperando' && (
        <p className={estilos.esperandoTexto}>
          Le llegó al Gerente operativo. Aquí aparece la respuesta apenas decida.
        </p>
      )}
    </article>
  )
}
